import { Settings, Zap, BarChart, Workflow, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import ConsultationModal from '@/components/ConsultationModal';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const AutomateWorkflows = () => {
  const { t } = useLanguage();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  const features = [
    {
      icon: Workflow,
      title: t('automateWorkflows.workflowAutomation'),
      description: t('automateWorkflows.workflowAutomationDescription')
    },
    {
      icon: Zap,
      title: t('automateWorkflows.taskOptimization'), 
      description: t('automateWorkflows.taskOptimizationDescription')
    },
    {
      icon: Settings,
      title: t('automateWorkflows.integrationPlatforms'),
      description: t('automateWorkflows.integrationPlatformsDescription')
    },
    {
      icon: BarChart,
      title: t('automateWorkflows.processMonitoring'),
      description: t('automateWorkflows.processMonitoringDescription')
    }
  ];

  const businessAreas = [
    { name: t('automateWorkflows.customerService'), description: t('automateWorkflows.customerServiceDesc') },
    { name: t('automateWorkflows.dataProcessing'), description: t('automateWorkflows.dataProcessingDesc') },
    { name: t('automateWorkflows.financeAccounting'), description: t('automateWorkflows.financeAccountingDesc') },
    { name: t('automateWorkflows.marketing'), description: t('automateWorkflows.marketingDesc') },
    { name: t('automateWorkflows.hrOperations'), description: t('automateWorkflows.hrOperationsDesc') },
    { name: t('automateWorkflows.inventoryManagement'), description: t('automateWorkflows.inventoryManagementDesc') },
    { name: t('automateWorkflows.qualityAssurance'), description: t('automateWorkflows.qualityAssuranceDesc') },
    { name: t('automateWorkflows.communications'), description: t('automateWorkflows.communicationsDesc') }
  ];

  const benefits = [
    { 
      title: t('automateWorkflows.increasedEfficiency'), 
      description: t('automateWorkflows.increasedEfficiencyDescription'),
      benefits: [
        t('automateWorkflows.twentyFourSevenOperation'), 
        t('automateWorkflows.fasterProcessing'), 
        t('automateWorkflows.resourceOptimization'), 
        t('automateWorkflows.scalableSolutions')
      ] 
    },
    { 
      title: t('automateWorkflows.errorReduction'), 
      description: t('automateWorkflows.errorReductionDescription'),
      benefits: [
        t('automateWorkflows.consistentOutput'), 
        t('automateWorkflows.dataAccuracy'), 
        t('automateWorkflows.complianceAssurance'), 
        t('automateWorkflows.qualityControl')
      ] 
    },
    { 
      title: t('automateWorkflows.costSavings'), 
      description: t('automateWorkflows.costSavingsDescription'),
      benefits: [
        t('automateWorkflows.lowerLaborCosts'), 
        t('automateWorkflows.reducedErrors'), 
        t('automateWorkflows.fasterTurnaround'), 
        t('automateWorkflows.betterResourceAllocation')
      ] 
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Back Button */}
      <section className="pt-24 pb-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link to="/">
            <Button variant="outline-light" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('jobs.backToHome')}
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center animate-fade-in">
            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-accent to-primary mb-8 glow-accent">
              <Settings className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold mb-6">
              {t('automateWorkflows.title')}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              {t('automateWorkflows.heroDescription')}
            </p>
            <Button 
              size="lg" 
              className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('automateWorkflows.startAutomating')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center mb-16">
            {t('automateWorkflows.ourServices')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:scale-105 hover:glow-primary animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-accent to-primary glow-accent mb-6">
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Areas */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center mb-16">
            {t('automateWorkflows.businessAreasTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {businessAreas.map((area, index) => (
              <div 
                key={area.name}
                className="text-center p-4 sm:p-6 rounded-2xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:scale-105 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-lg sm:text-xl font-display font-bold mb-2 gradient-text">
                  {area.name}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Automation Benefits */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center mb-16">
            {t('automateWorkflows.automationBenefits')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={benefit.title}
                className="p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 animate-fade-in"
                style={{ animationDelay: `${index * 0.3}s` }}
              >
                <h3 className="text-2xl font-display font-bold mb-4 gradient-text">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {benefit.description}
                </p>
                <ul className="space-y-2">
                  {benefit.benefits.map((item) => (
                    <li key={item} className="flex items-center text-sm text-foreground">
                      <div className="w-2 h-2 bg-primary rounded-full mr-3 glow-primary"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold mb-6">
            {t('automateWorkflows.readyToAutomate')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            {t('automateWorkflows.readyToAutomateDescription')}
          </p>
          <Button 
            size="lg" 
            className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
            onClick={() => setIsConsultationOpen(true)}
          >
            {t('automateWorkflows.startAutomationJourney')}
          </Button>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </div>
  );
};

export default AutomateWorkflows;